/**
 * LawLab Signup
 */
import { axiosRequest } from '@/plugins/axios-common'

import { handleError } from '@/store/handle-error'

export const SignupIntegrationState = {
  signupIntegration: {
    customer: {},
    invoices: [],
    invoice: {},
    isLoadingSubscriptionUpdate: false,
    isSearching: false,
    paginationOptions: {
      page: 1,
      itemsPerPage: 20,
      sortBy: [],
      sortDesc: [],
    },
    plans: [],
    paymentMethods: [],
    subscription: {
      id: 'my-subscription',
    },
    usage: {},
  },
}

export const SignupIntegrationActions = {
  destroySignupSubscriptionPaymentMethod: async ({ commit }, req) => {
    commit('setSearchLoading', true)

    return axiosRequest.
      delete(`/ext/integrations/signup/subscriptions/my-subscription/payment_methods/${req.id}`).
      then(({ data }) => {
        return data
      }).
      catch(handleError).
      finally(_ => commit('setSearchLoading', false))
  },

  evaluateSignupSubscription: async ({ commit }, req) => {
    commit('setSearchLoading', true)

    return axiosRequest.
      post('/ext/integrations/signup/subscriptions/my-subscription/evaluate', req).
      then(({ data }) => {
        return data
      }).
      catch(handleError).
      finally(_ => commit('setSearchLoading', false))
  },

  getSignupCustomer: async ({ commit }, req) => {
    commit('setSearchLoading', true)

    return axiosRequest.
      get('/ext/integrations/signup/subscriptions/my-subscription/customers/my-customer').
      then(({ data }) => {
        commit('setSignupCustomer', data.customer || {})
      }).
      catch(handleError).
      finally(_ => commit('setSearchLoading', false))
  },

  getSignupSubscription: async ({ commit }, req) => {
    commit('setSearchLoading', true)

    return axiosRequest.
      get('/ext/integrations/signup/subscriptions/my-subscription').
      then(({ data }) => {
        commit('setSignupSubscription', data.subscription || {})
        commit('setSignupSubscriptionPlans', data.plans || [])
      }).
      catch(handleError).
      finally(_ => commit('setSearchLoading', false))
  },

  getSignupSubscriptionPaymentMethods: async ({ commit }, req) => {
    commit('setSearchLoading', true)

    return axiosRequest.
      get('/ext/integrations/signup/subscriptions/my-subscription/payment_methods').
      then(({ data }) => {
        commit('setSignupSubscriptionPaymentMethods', data.payment_methods || [])
      }).
      catch(handleError).
      finally(_ => commit('setSearchLoading', false))
  },

  getSignupSubscriptionUsage: async ({ commit }, req) => {
    return axiosRequest.
      get('/ext/integrations/signup/subscriptions/my-subscription/usage').
      then(({ data }) => {
        commit('setSignupSubscriptionUsage', data || {})
      }).
      catch(handleError)
  },

  getSignupInvoices: async ({ commit }, req) => {
    commit('setSearchLoading', true)

    return axiosRequest.
      get('/ext/integrations/signup/subscriptions/my-subscription/invoices', { params: req.params }).
      then(({ data }) => {
        commit('setSignupInvoices', data.invoices || {})
      }).
      catch(handleError).
      finally(_ => commit('setSearchLoading', false))
  },

  getSignupInvoice: async ({ commit }, req) => {
    commit('setSearchLoading', true)

    return axiosRequest.
      get(`/ext/integrations/signup/subscriptions/my-subscription/invoices/${req.id}`).
      then(({ data }) => {
        commit('setSignupInvoice', data.invoice || {})
      }).
      catch(handleError).
      finally(_ => commit('setSearchLoading', false))
  },

  downloadInvoice: async ({ commit }, req) => {
    return axiosRequest.
      get(`/ext/integrations/signup/subscriptions/my-subscription/invoices/${req.id}/download_invoice`).
      then(({ data }) => {
        return data
      }).
      catch(handleError).
      finally(_ => commit('setSearchLoading', false))
  },

  downloadReceipt: async ({ commit }, req) => {
    return axiosRequest.
      get(`/ext/integrations/signup/subscriptions/my-subscription/invoices/${req.id}/download_receipt`).
      then(({ data }) => {
        return data
      }).
      catch(handleError).
      finally(_ => commit('setSearchLoading', false))
  },

  setSignupSubscriptionPaymentMethodAsDefault: async ({ commit }, req) => {
    commit('setSearchLoading', true)

    return axiosRequest.
      post(`/ext/integrations/signup/subscriptions/my-subscription/payment_methods/${req.id}/default`).
      then(({ data }) => {
        commit('setSignupSubscriptionPaymentMethods', data.payment_methods || [])
      }).
      catch(handleError).
      finally(_ => commit('setSearchLoading', false))
  },

  setPaginationOptions ({ commit }, options) {
    commit('setPaginationOptions', options)
  },

  signupSubscriptionPaymentMethodsCreate: async ({ commit }, req) => {
    commit('setSearchLoading', true)

    return axiosRequest.
      post('/ext/integrations/signup/subscriptions/my-subscription/payment_methods', req.params).
      then(({ data }) => {
        return data.payment_methods
      }).
      catch(handleError).
      finally(_ => commit('setSearchLoading', false))
  },

  signupSubscriptionPaymentMethodCreateSetupIntent: async ({ commit }, req) => {
    commit('setSearchLoading', true)

    return axiosRequest.
      post('/ext/integrations/signup/subscriptions/my-subscription/setup_intents').
      then(({ data }) => {
        return data.setup_intents
      }).
      catch(handleError).
      finally(_ => commit('setSearchLoading', false))
  },

  updateSignupCustomer: async ({ commit }, req) => {
    commit('setSearchLoading', true)

    return axiosRequest.
      patch('/ext/integrations/signup/subscriptions/my-subscription/customers/my-customer', req.params).
      then(({ data }) => {
        commit('setSignupCustomer', data.customer || {})
      }).
      catch(handleError).
      finally(_ => commit('setSearchLoading', false))
  },

  updateSignupSubscription: async ({ commit }, req) => {
    commit('setIsLoadingSubscriptionUpdate', true)

    return axiosRequest.
      patch('/ext/integrations/signup/subscriptions/my-subscription', req.params).
      then(({ data }) => {
        commit('setSignupSubscription', data.subscription || {})
      }).
      catch(handleError).
      finally(_ => commit('setIsLoadingSubscriptionUpdate', false))
  },
}

export const SignupIntegrationGetters = {
  isLoading: state => state.signupIntegration.isSearching,
  isLoadingSubscriptionUpdate: state => state.signupIntegration.isLoadingSubscriptionUpdate,
  paginationOptions: state => state.signupIntegration.paginationOptions,
  signupCustomer: state => state.signupIntegration.customer,
  signupInvoices: state => state.signupIntegration.invoices,
  signupInvoice: state => state.signupIntegration.invoice,
  signupSubscription: state => state.signupIntegration.subscription,
  signupSubscriptionPaymentMethods: state => state.signupIntegration.paymentMethods,
  signupSubscriptionPlans: state => state.signupIntegration.plans,
  signupSubscriptionUsage: state => state.signupIntegration.usage,
}

export const SignupIntegrationMutations = {
  setSignupCustomer: (state, value) => {
    state.signupIntegration.customer = value
  },

  setSignupInvoices: (state, value) => {
    state.signupIntegration.invoices = value
  },

  setSignupInvoice: (state, value) => {
    state.signupIntegration.invoice = value
  },

  setPaginationOptions: (state, value) => {
    state.signupIntegration.paginationOptions = value
  },

  setIsLoadingSubscriptionUpdate: (state, value) => {
    state.signupIntegration.isLoadingSubscriptionUpdate = value
  },

  setSearchLoading: (state, value) => {
    state.signupIntegration.isSearching = value
  },

  setSignupSubscriptionPlans: (state, value) => {
    state.signupIntegration.plans = value
  },

  setSignupSubscriptionPaymentMethods: (state, value) => {
    const data = value.map(card => {
      card.processingSetDefault = false
      card.processingDelete = false

      return card
    })
    state.signupIntegration.paymentMethods = data
  },

  setSignupSubscriptionUsage: (state, value) => {
    state.signupIntegration.usage = value
  },

  setSignupSubscription: (state, value) => {
    state.signupIntegration.subscription = value
  },
}
